/**
 * structuredNotesMove.js
 *
 * Shared, pure helpers for the bullet-style "smart notes" used by
 * StructuredNotesEditor. These were previously duplicated inline in every
 * consumer that supports dragging a note from one box to another (Observation,
 * Adult/CYP ASD SSI, Adult Autism History). Centralising them keeps a single
 * source of truth for the move semantics and the note-ID scheme, so the
 * codebase has exactly one way to do this rather than five subtly different
 * copies.
 *
 * Why these helpers matter:
 *   - moveNoteBetweenArrays performs the array surgery for a cross-box note
 *     move (remove from source, insert into target at the drop index). It is
 *     pure: it never mutates the inputs, which makes it safe to call inside a
 *     React setState updater and trivial to unit-test.
 *   - makeNoteId produces collision-free IDs. The old scheme used Date.now(),
 *     which can return the same value for two notes created in the same
 *     millisecond (e.g. across two different note boxes). Colliding IDs break
 *     both React keys and the move logic (which finds a note by id), so a
 *     monotonic counter is mixed into the timestamp to guarantee uniqueness.
 *   - parsePanelNotesKey splits the "sectionId-subKey" panel key used by the
 *     Adult Autism History module back into its parts so the move handler can
 *     build the correct Firestore field paths.
 */

// Monotonic counter mixed into each generated ID. Wraps well below any realistic
// number of notes created in a single page session, purely to disambiguate two
// IDs minted within the same millisecond.
let noteIdCounter = 0;

/**
 * Generate a collision-free note ID.
 *
 * Returns a string of the form `${timestamp}-${counter}`. IDs are opaque and
 * are only ever compared with === (never parsed as numbers), so mixing these
 * string IDs with any legacy numeric IDs already stored in Firestore is safe.
 *
 * @returns {string} A unique note identifier.
 */
export function makeNoteId() {
  noteIdCounter = (noteIdCounter + 1) % 1e6;
  return `${Date.now()}-${noteIdCounter}`;
}

/**
 * Move a single note from one notes array to another.
 *
 * Pure: the input arrays are never mutated. Returns fresh `source` and `target`
 * arrays with the note removed from the source and spliced into the target at
 * `insertAt` (appended when `insertAt` is null/undefined or out of range).
 *
 * Returns `null` when the note is not present in `sourceNotes`, so callers can
 * cleanly no-op (mirrors the previous inline handlers, which bailed out early
 * when the dragged note could not be found).
 *
 * @param {Array<object>} sourceNotes - Notes array the note is moving from.
 * @param {Array<object>} targetNotes - Notes array the note is moving into.
 * @param {string|number} noteId - ID of the note being moved.
 * @param {number|null} [insertAt] - Index in the target to insert at; appends when null/out of range.
 * @param {object|null} [notePatch] - Optional fields to merge onto the moved note (e.g. in-progress
 *   edit text from StructuredNotesEditor before a cross-box Move).
 * @returns {{ source: Array<object>, target: Array<object> }|null}
 */
export function moveNoteBetweenArrays(sourceNotes, targetNotes, noteId, insertAt, notePatch = null) {
  const safeSource = Array.isArray(sourceNotes) ? sourceNotes : [];
  const safeTarget = Array.isArray(targetNotes) ? targetNotes : [];

  const movedNote = safeSource.find((n) => n.id === noteId);
  if (!movedNote) return null;

  const noteToMove = notePatch ? { ...movedNote, ...notePatch } : movedNote;

  const source = safeSource.filter((n) => n.id !== noteId);
  const target = [...safeTarget];
  if (insertAt != null && insertAt >= 0 && insertAt <= target.length) {
    target.splice(insertAt, 0, noteToMove);
  } else {
    target.push(noteToMove);
  }

  return { source, target };
}

/**
 * Split a panel notes key ("sectionId-subKey") into its parts.
 *
 * The Adult Autism History module keys each subsection's notes by
 * `${sectionId}-${subKey}`. We split on the LAST hyphen so that section IDs
 * which themselves contain hyphens are preserved intact (the subKey is always
 * the final segment).
 *
 * @param {string} key - The composite panel key, e.g. "3-2".
 * @returns {{ sectionId: string, subKey: string }}
 */
export function parsePanelNotesKey(key) {
  const str = String(key ?? '');
  const idx = str.lastIndexOf('-');
  if (idx === -1) return { sectionId: str, subKey: '' };
  return { sectionId: str.slice(0, idx), subKey: str.slice(idx + 1) };
}
